import { RouteFragment } from "@impero-com/common/routeFragments";
import { TableRestoreConfig, withRestoreParam } from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import { z } from "zod";

import { LegacyRoute } from "../../legacyRoutes";
import { WebRoute } from "../../routes";

import {
    buildTestingActivityListTableStateStorageKey,
    testingActivityListColumnFiltersCodecMap,
    testingActivityListDataParamsCodecMap,
    testingActivityListTableDefaultValues,
} from "./testingActivity/list/consts";
import {
    buildTestingControlListTableStateStorageKey,
    testingControlListColumnFiltersCodecMap,
    testingControlListTableDefaultValues,
} from "./testingControl/list/consts";

// Declares module scoped routes with reactive params. Eventually, we may want to module scope all routes

export const TESTING_PROGRAM_ID_QUERY_PARAM = "testing_program_id";
export const TESTING_CONTROL_CREATE_MODE_PARAM = "mode";
export const BULK_CREATE_MODE = "bulk";

export const testingControlCreateModeParamSchema = z
    .enum([BULK_CREATE_MODE])
    .nullable();
export const testingProgramIdParamSchema = z.coerce.number().int().positive();
export const testingControlIdParamSchema = z.guid();
export const controlTestingActivityIdParamSchema = z.guid();

type TestingProgramId = number;
type TestingControlId = string;
type TestingActivityId = string;

export const buildTestingProgramViewEditUrl = (
    testingProgramId: TestingProgramId
) => `${WebRoute.TESTING_PROGRAMS}/${testingProgramId}`;
export const buildTestingControlViewEditUrl = (
    testingControlId: TestingControlId
) => `${WebRoute.TESTING_CONTROLS}/${testingControlId}`;
export const buildTestingActivityViewEditUrl = (
    testingActivityId: TestingActivityId
) => `${WebRoute.TESTING_ACTIVITIES}/${testingActivityId}`;
export const testingProgramListUrl = withRestoreParam(
    WebRoute.TESTING_PROGRAMS
);
export const buildTestingActivitiesForProgramUrl = (
    testingProgramId: TestingProgramId
) =>
    withRestoreParam(
        `${WebRoute.TESTING_PROGRAMS}/${testingProgramId}/${RouteFragment.TESTING_ACTIVITIES}`
    );
export const buildTestControlCreateUrl = (testingProgramId: TestingProgramId) =>
    `${WebRoute.TESTING_CONTROLS_NEW}?${TESTING_PROGRAM_ID_QUERY_PARAM}=${testingProgramId}`;
export const buildTestControlCreateBulkUrl = (
    testingProgramId: TestingProgramId
) =>
    `${WebRoute.TESTING_CONTROLS_NEW}?${TESTING_PROGRAM_ID_QUERY_PARAM}=${testingProgramId}&${TESTING_CONTROL_CREATE_MODE_PARAM}=${BULK_CREATE_MODE}`;
export const buildTestingProgramTestingControlsListUrl = (
    testingProgramId: TestingProgramId
) =>
    withRestoreParam(
        `${WebRoute.TESTING_PROGRAMS}/${testingProgramId}/${RouteFragment.TESTING_CONTROLS}`
    );
export const buildLegacyControlActivityUrl = (activityId: string) =>
    `${LegacyRoute.CONTROL_RESULT}/${activityId}`;

export function buildTestingControlListRestoreConfig(
    testingProgramId: number
): TableRestoreConfig {
    return {
        tableId: buildTestingControlListTableStateStorageKey(testingProgramId),
        defaultValues: testingControlListTableDefaultValues,
        codecMap: testingControlListColumnFiltersCodecMap,
        onError: Sentry.captureException,
    };
}

export function buildTestingActivityListRestoreConfig(
    testingProgramId: number
): TableRestoreConfig {
    return {
        tableId: buildTestingActivityListTableStateStorageKey(testingProgramId),
        defaultValues: testingActivityListTableDefaultValues,
        codecMap: testingActivityListColumnFiltersCodecMap,
        onError: Sentry.captureException,
        dataParamsCodecMap: testingActivityListDataParamsCodecMap,
    };
}
